import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Buildings, Plus, Trash, EnvelopeSimple, CheckCircle, X } from '@phosphor-icons/react';
import { API } from '../context/AuthContext';
import { toast } from 'sonner';

/**
 * Phase 2.5a — Homeowner-side widget to invite + revoke builder access.
 *
 * Sits on the property page so the homeowner can hand a builder a
 * permanent dashboard link instead of having to re-share a magic URL.
 * Builder doesn't need an account — when they sign in to /builder/login
 * with this exact email, the property auto-shows on their dashboard.
 *
 * Decisions baked in:
 *   - Homeowner-initiated only (Decision 2a — we'll consider 2b later)
 *   - Revoke is non-destructive — keeps the audit row, surfaces it as
 *     historical (status 'revoked') so the homeowner can re-invite if
 *     they change their mind without losing context.
 */
export default function PropertyBuildersCard({ propertyId }) {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInvite, setShowInvite] = useState(false);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await axios.get(`${API}/properties/${propertyId}/builders`, { withCredentials: true });
            setLinks(r.data?.builders || []);
        } catch { /* surface only on user actions */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (propertyId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [propertyId]);

    const invite = async (e) => {
        e?.preventDefault?.();
        const trimmed = email.trim().toLowerCase();
        if (!trimmed || !trimmed.includes('@')) { toast.error('Enter a valid email address'); return; }
        setBusy(true);
        try {
            await axios.post(
                `${API}/properties/${propertyId}/builders`,
                { email: trimmed, name: name.trim() || undefined },
                { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
            );
            toast.success(`${trimmed} can now sign in at /builder/login.`);
            setEmail(''); setName(''); setShowInvite(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not invite. Try again.');
        } finally {
            setBusy(false);
        }
    };

    const revoke = (link) => {
        const t = toast(`Revoke ${link.builder_email}?`, {
            description: 'They\'ll lose access to this property immediately. They can be re-invited any time.',
            duration: 10000,
            action: {
                label: 'Revoke',
                onClick: async () => {
                    try {
                        await axios.delete(
                            `${API}/properties/${propertyId}/builders/${encodeURIComponent(link.builder_email)}`,
                            { withCredentials: true },
                        );
                        toast.success('Access revoked.');
                        load();
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not revoke. Try again.');
                    }
                },
            },
            cancel: { label: 'Cancel', onClick: () => toast.dismiss(t) },
        });
    };

    const active = links.filter(l => l.status === 'active');
    const revoked = links.filter(l => l.status === 'revoked');

    if (loading) return null;

    return (
        <div className="bg-white border border-[#E4E4E7] p-4 mt-4" data-testid="property-builders-card">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Buildings size={18} weight="duotone" color="#1FA5CF" />
                    <div className="font-display font-semibold text-sm truncate">Builders with access</div>
                </div>
                <button
                    onClick={() => setShowInvite(s => !s)}
                    data-testid="invite-builder-toggle"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-[#1FA5CF] text-[#1FA5CF] hover:bg-[#E8F5FB] font-mono text-[10px] uppercase tracking-wider"
                >
                    <Plus size={12} weight="bold" /> Invite
                </button>
            </div>

            {showInvite && (
                <form onSubmit={invite} className="mt-3 border-t border-[#E4E4E7] pt-3" data-testid="invite-builder-form">
                    <label className="label-tiny block">Builder email</label>
                    <div className="relative mt-1">
                        <EnvelopeSimple size={14} weight="duotone" color="#1FA5CF" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="dave@example-builders.co.uk"
                            data-testid="invite-builder-email-input"
                            className="w-full pl-9 pr-3 py-2 border border-[#E4E4E7] focus:border-[#18181B] outline-none text-sm font-mono"
                            autoFocus
                            spellCheck={false}
                        />
                    </div>
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Their name (optional)"
                        data-testid="invite-builder-name-input"
                        className="mt-2 w-full px-3 py-2 border border-[#E4E4E7] focus:border-[#18181B] outline-none text-sm"
                        maxLength={120}
                    />
                    <div className="mt-3 flex gap-2">
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="invite-builder-submit"
                            className="flex-1 bg-[#1FA5CF] hover:bg-[#1787AB] text-white py-2 font-mono text-xs uppercase tracking-[0.2em] disabled:opacity-50 active:scale-95 transition-all duration-150"
                        >{busy ? 'Inviting…' : 'Send invite'}</button>
                        <button
                            type="button"
                            onClick={() => { setShowInvite(false); setEmail(''); setName(''); }}
                            className="px-3 py-2 border border-[#18181B] hover:bg-[#F4F4F5] font-mono text-xs uppercase tracking-[0.2em]"
                        >Cancel</button>
                    </div>
                    <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#A1A1AA]">
                        They sign in via magic link at /builder/login — no account needed
                    </p>
                </form>
            )}

            {active.length === 0 && !showInvite && (
                <p className="mt-3 text-xs text-[#52525B]" data-testid="no-builders-empty">
                    No builders linked yet. Invite one above to give them a permanent dashboard rather than a one-off share link.
                </p>
            )}

            {active.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="active-builders-list">
                    {active.map(link => (
                        <li key={link.builder_email} className="flex items-center gap-3 py-2 border-b border-[#F4F4F5] last:border-0">
                            <CheckCircle size={14} weight="fill" color="#1FA5CF" className="flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{link.builder_name || link.builder_email}</div>
                                <div className="text-[10px] text-[#A1A1AA] font-mono uppercase tracking-wider truncate">
                                    {link.builder_name && `${link.builder_email} · `}
                                    {link.builder_id ? 'Active' : 'Pending sign-in'}
                                </div>
                            </div>
                            <button
                                onClick={() => revoke(link)}
                                data-testid={`revoke-builder-${link.builder_email}`}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[#52525B] hover:text-[#E53935] font-mono text-[10px] uppercase tracking-wider"
                            >
                                <Trash size={11} /> Revoke
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {revoked.length > 0 && (
                <details className="mt-3" data-testid="revoked-builders-details">
                    <summary className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#A1A1AA] cursor-pointer hover:text-[#52525B]">
                        Previously revoked ({revoked.length})
                    </summary>
                    <ul className="mt-2 space-y-1">
                        {revoked.map(link => (
                            <li key={link.builder_email} className="flex items-center gap-2 text-xs text-[#A1A1AA] line-through">
                                <X size={12} />
                                <span className="truncate">{link.builder_email}</span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}
